import { invokeFunction, invokePublicFunction } from '../api'
import { toApiRequestError } from '../errors'

export type BillingTier = 'standalone' | 'consultant' | 'enterprise'
export type BillingInterval = 'monthly' | 'annual'
export type ModuleBillingKey =
  | 'qapi'
  | 'fire_safety'
  | 'acquisition'
  | 'notifications'
  | 'compliance_charts'
  | 'corporate_actions'
  | 'branded_exports'
  | 'training_recommendations'
  | 'insurance_verification'
  | 'patient_comms'
  | 'pat_automation'

export interface CheckoutSessionRequest {
  tier: BillingTier
  billing_interval: BillingInterval
}

export interface CheckoutSessionResponse {
  url: string
  session_id: string
  tier: BillingTier
  billing_interval: BillingInterval
  facility_count: number
}

export interface ModuleBillingPreview {
  total_delta_cents: number | null
  currency: string | null
  generated_at: string
  quantity: number
}

export interface ModuleBillingState {
  module_key: ModuleBillingKey
  title: string
  description: string
  billing_interval: BillingInterval
  facility_count: number
  billed_quantity: number
  enabled_facility_count: number
  entitlement_status: string
  has_active_subscription_item: boolean
  preview: ModuleBillingPreview | null
}

export interface ModuleBillingListResponse {
  subscription_status: string
  billing_interval: BillingInterval
  modules: ModuleBillingState[]
}

export interface ModuleBillingMutationResponse {
  subscription_status: string
  module: ModuleBillingState
}

export interface BillingPortalSummary {
  organization_name: string
  current_tier: BillingTier
  subscription_status: string
  billing_interval: BillingInterval | null
  stripe_customer_configured: boolean
  stripe_subscription_id: string | null
  stripe_price_id: string | null
  current_period_end: string | null
  trial_ends_at: string | null
}

export interface BillingPortalSessionRequest {
  return_path?: string
}

export interface BillingPortalSessionResponse {
  url: string
}

export interface PublicSignupRequest {
  organization_name: string
  contact_name: string
  contact_email: string
  contact_phone?: string
  facility_name: string
  facility_type?: string
  address: string
  city: string
  state: string
  zip_code: string
  admin_email: string
  password: string
  tier: BillingTier
  billing_interval: BillingInterval
}

export interface PublicSignupResponse {
  tenant_id: string
  admin_email: string
  tier: BillingTier
  billing_interval: BillingInterval
  trial_days: number
  next_path: string
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

export async function createCheckoutSession(input: CheckoutSessionRequest): Promise<CheckoutSessionResponse> {
  const { data, error } = await invokeFunction<unknown>('checkout-session', {
    method: 'POST',
    path: '/',
    body: input,
  })

  if (error) throw toApiRequestError(error, 'Failed to create Stripe checkout session')
  if (!isRecord(data)) throw new Error('Malformed checkout session response from server')
  if (
    typeof data.url !== 'string' ||
    typeof data.session_id !== 'string' ||
    typeof data.tier !== 'string' ||
    typeof data.billing_interval !== 'string' ||
    typeof data.facility_count !== 'number'
  ) {
    throw new Error('Malformed checkout session response from server')
  }

  return {
    url: data.url,
    session_id: data.session_id,
    tier: data.tier as BillingTier,
    billing_interval: data.billing_interval as BillingInterval,
    facility_count: data.facility_count,
  }
}

function parseModulePreview(value: unknown): ModuleBillingPreview | null {
  if (!isRecord(value)) return null
  if (typeof value.generated_at !== 'string' || typeof value.quantity !== 'number') return null
  return {
    total_delta_cents: typeof value.total_delta_cents === 'number' ? value.total_delta_cents : null,
    currency: typeof value.currency === 'string' ? value.currency : null,
    generated_at: value.generated_at,
    quantity: value.quantity,
  }
}

function parseModuleBillingState(value: unknown): ModuleBillingState {
  if (!isRecord(value)) throw new Error('Malformed module billing response from server')
  if (
    typeof value.module_key !== 'string' ||
    typeof value.title !== 'string' ||
    typeof value.description !== 'string' ||
    typeof value.billing_interval !== 'string' ||
    typeof value.facility_count !== 'number' ||
    typeof value.billed_quantity !== 'number' ||
    typeof value.enabled_facility_count !== 'number' ||
    typeof value.entitlement_status !== 'string' ||
    typeof value.has_active_subscription_item !== 'boolean'
  ) {
    throw new Error('Malformed module billing response from server')
  }

  return {
    module_key: value.module_key as ModuleBillingKey,
    title: value.title,
    description: value.description,
    billing_interval: value.billing_interval as BillingInterval,
    facility_count: value.facility_count,
    billed_quantity: value.billed_quantity,
    enabled_facility_count: value.enabled_facility_count,
    entitlement_status: value.entitlement_status,
    has_active_subscription_item: value.has_active_subscription_item,
    preview: parseModulePreview(value.preview),
  }
}

export async function getModuleBillingState(): Promise<ModuleBillingListResponse> {
  const { data, error } = await invokeFunction<unknown>('billing-modules', {
    method: 'GET',
    path: '/',
  })

  if (error) throw toApiRequestError(error, 'Failed to load module billing state')
  if (!isRecord(data) || typeof data.subscription_status !== 'string' || typeof data.billing_interval !== 'string' || !Array.isArray(data.modules)) {
    throw new Error('Malformed module billing response from server')
  }

  return {
    subscription_status: data.subscription_status,
    billing_interval: data.billing_interval as BillingInterval,
    modules: data.modules.map(parseModuleBillingState),
  }
}

export async function getBillingPortalSummary(): Promise<BillingPortalSummary> {
  const { data, error } = await invokeFunction<unknown>('billing-portal', {
    method: 'GET',
    path: '/',
  })

  if (error) throw toApiRequestError(error, 'Failed to load billing workspace')
  if (
    !isRecord(data) ||
    typeof data.organization_name !== 'string' ||
    typeof data.current_tier !== 'string' ||
    typeof data.subscription_status !== 'string' ||
    typeof data.stripe_customer_configured !== 'boolean'
  ) {
    throw new Error('Malformed billing workspace response from server')
  }

  return {
    organization_name: data.organization_name,
    current_tier: data.current_tier as BillingTier,
    subscription_status: data.subscription_status,
    billing_interval:
      data.billing_interval === 'monthly' || data.billing_interval === 'annual'
        ? (data.billing_interval as BillingInterval)
        : null,
    stripe_customer_configured: data.stripe_customer_configured,
    stripe_subscription_id: typeof data.stripe_subscription_id === 'string' ? data.stripe_subscription_id : null,
    stripe_price_id: typeof data.stripe_price_id === 'string' ? data.stripe_price_id : null,
    current_period_end: typeof data.current_period_end === 'string' ? data.current_period_end : null,
    trial_ends_at: typeof data.trial_ends_at === 'string' ? data.trial_ends_at : null,
  }
}

export async function createBillingPortalSession(
  input: BillingPortalSessionRequest = {},
): Promise<BillingPortalSessionResponse> {
  const { data, error } = await invokeFunction<unknown>('billing-portal', {
    method: 'POST',
    path: '/',
    body: input,
  })

  if (error) throw toApiRequestError(error, 'Failed to open Stripe billing portal')
  if (!isRecord(data) || typeof data.url !== 'string') {
    throw new Error('Malformed billing portal response from server')
  }

  return { url: data.url }
}

export async function createPublicSignup(input: PublicSignupRequest): Promise<PublicSignupResponse> {
  const { data, error } = await invokePublicFunction<unknown>('public-signup', {
    method: 'POST',
    path: '/',
    body: input,
  })

  if (error) throw toApiRequestError(error, 'Failed to create trial account')
  if (
    !isRecord(data) ||
    typeof data.tenant_id !== 'string' ||
    typeof data.admin_email !== 'string' ||
    typeof data.tier !== 'string' ||
    typeof data.billing_interval !== 'string' ||
    typeof data.trial_days !== 'number' ||
    typeof data.next_path !== 'string'
  ) {
    throw new Error('Malformed public signup response from server')
  }

  return {
    tenant_id: data.tenant_id,
    admin_email: data.admin_email,
    tier: data.tier as BillingTier,
    billing_interval: data.billing_interval as BillingInterval,
    trial_days: data.trial_days,
    next_path: data.next_path,
  }
}

export async function mutateModuleBilling(
  moduleKey: ModuleBillingKey,
  enabled: boolean,
  previewOnly: boolean,
): Promise<ModuleBillingMutationResponse> {
  const { data, error } = await invokeFunction<unknown>('billing-modules', {
    method: 'POST',
    path: '/',
    body: {
      module_key: moduleKey,
      enabled,
      preview_only: previewOnly,
    },
  })

  if (error) throw toApiRequestError(error, 'Failed to update module billing')
  if (!isRecord(data) || typeof data.subscription_status !== 'string') {
    throw new Error('Malformed module billing response from server')
  }

  return {
    subscription_status: data.subscription_status,
    module: parseModuleBillingState(data.module),
  }
}

export interface FieldSurveyorSoloSignupRequest {
  email: string
  password: string
  first_name?: string
  last_name?: string
  facility_name: string
  facility_type?: string
}

export interface FieldSurveyorSoloSignupResponse {
  tenant_id: string
  email: string
  next_path: string
}

export async function createFieldSurveyorSoloSignup(
  input: FieldSurveyorSoloSignupRequest,
): Promise<FieldSurveyorSoloSignupResponse> {
  const { data, error } = await invokePublicFunction<unknown>('public-signup', {
    method: 'POST',
    path: '/field-surveyor-solo',
    body: input,
  })
  if (error) throw toApiRequestError(error, 'Failed to create solo surveyor account')
  const d = data as Record<string, unknown>
  if (!d || typeof d.tenant_id !== 'string' || typeof d.email !== 'string' || typeof d.next_path !== 'string') {
    throw new Error('Unexpected response shape')
  }
  return { tenant_id: d.tenant_id, email: d.email, next_path: d.next_path }
}
